import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatMoney } from '../lib/currency';
import {
  GraduationCap, MapPin, Star, BookOpen, Users, CheckCircle2,
  Globe, Mail, Phone, ExternalLink, Clock, PoundSterling,
  ArrowRight, ChevronLeft, Loader2
} from 'lucide-react';


const typeLabels = {
  university: 'University',
  college: 'College',
  training_provider: 'Training Provider',
};

const typeColors = {
  university: 'bg-blue-100 text-blue-700',
  college: 'bg-emerald-100 text-emerald-700',
  training_provider: 'bg-purple-100 text-purple-700',
};

const levelColors = {
  undergraduate: 'bg-blue-50 text-blue-700',
  postgraduate: 'bg-purple-50 text-purple-700',
  beginner: 'bg-emerald-50 text-emerald-700',
  intermediate: 'bg-amber-50 text-amber-700',
  advanced: 'bg-rose-50 text-rose-700',
};

function StarRating({ rating, size = 'normal' }) {
  const sz = size === 'small' ? 'w-3.5 h-3.5' : 'w-5 h-5';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={`${sz} ${s <= Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}`}
        />
      ))}
    </div>
  );
}

function StarSelector({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onChange(s)}
          onMouseEnter={() => setHovered(s)}
          onMouseLeave={() => setHovered(0)}
        >
          <Star
            className={`w-6 h-6 cursor-pointer transition-colors ${
              s <= (hovered || value) ? 'text-amber-400 fill-amber-400' : 'text-gray-300 fill-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

export default function UniversityPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, isAuthenticated } = useAuth();
  const [university, setUniversity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [reviewForm, setReviewForm] = useState({ rating: 0, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [applyModal, setApplyModal] = useState(null); // holds { course }
  const [applyForm, setApplyForm] = useState({ personal_statement: '', intake: 'September 2026', visa_required: false });
  const [applySubmitting, setApplySubmitting] = useState(false);

  useEffect(() => {
    fetchUniversity();
  }, [id]);

  const fetchUniversity = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/universities/${id}`);
      setUniversity(res.data);
    } catch {
      setUniversity(null);
    } finally {
      setLoading(false);
    }
  };

  const submitCourseApplication = async (e) => {
    e.preventDefault();
    if (!applyModal) return;
    setApplySubmitting(true);
    try {
      await api.post(`/universities/${university.id}/courses/${applyModal.course.id}/apply`, applyForm);
      toast.success('Application submitted! The university will review your application.');
      setApplyModal(null);
      setApplyForm({ personal_statement: '', intake: 'September 2026', visa_required: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit application');
    } finally { setApplySubmitting(false); }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (reviewForm.rating === 0) {
      toast.error('Please select a star rating');
      return;
    }
    setSubmittingReview(true);
    try {
      await api.post(`/universities/${id}/rate`, reviewForm);
      toast.success('Review submitted successfully!');
      setReviewForm({ rating: 0, comment: '' });
      fetchUniversity();
    } catch {
      toast.error('Failed to submit review. Please try again.');
    } finally {
      setSubmittingReview(false);
    }
  };

  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'courses', label: 'Courses' },
    { id: 'reviews', label: 'Reviews' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4F8]">
        <div className="h-48 bg-gradient-to-r from-blue-700 to-indigo-700" />
        <div className="max-w-4xl mx-auto px-4 -mt-12 pb-12">
          <Skeleton className="h-24 w-24 rounded-full mb-4" />
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-5 w-40" />
        </div>
      </div>
    );
  }

  if (!university) return null;

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      {/* Cover Banner */}
      <div className="bg-gradient-to-r from-blue-700 to-indigo-700 pt-6 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-white/70 hover:text-white text-sm mb-6 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Universities
          </button>
        </div>
      </div>

      {/* Profile Section */}
      <div className="max-w-4xl mx-auto px-4 -mt-12 mb-8">
        <Card className="shadow-lg">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-5 items-start">
              {/* Logo circle */}
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-200 flex items-center justify-center shadow-md shrink-0">
                <GraduationCap className="w-10 h-10 text-[#003366]" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-start gap-2 mb-2">
                  <h1 className="text-2xl font-bold text-foreground">{university.name}</h1>
                  {university.is_verified && (
                    <span className="flex items-center gap-1 bg-blue-50 text-blue-700 text-xs font-medium px-2 py-1 rounded-full">
                      <CheckCircle2 className="w-3 h-3" />
                      Verified
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  <Badge className={typeColors[university.type] || 'bg-gray-100 text-gray-700'}>
                    {typeLabels[university.type] || university.type}
                  </Badge>
                  <span className="flex items-center gap-1 text-sm text-muted-foreground">
                    <MapPin className="w-3.5 h-3.5" />
                    {university.location}
                  </span>
                </div>
                {university.average_rating > 0 && (
                  <div className="flex items-center gap-2">
                    <StarRating rating={university.average_rating} />
                    <span className="text-sm font-medium">{university.average_rating?.toFixed(1)}</span>
                  </div>
                )}
              </div>

              <Link to={`/register?role=student`} className="shrink-0">
                <Button className="bg-[#E11D48] hover:bg-[#E11D48]/90 text-white">
                  Apply to Study Here
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tab Bar */}
      <div className="max-w-4xl mx-auto px-4 mb-6">
        <div className="flex gap-1 bg-white rounded-xl p-1.5 shadow-sm border w-fit">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-[#003366] text-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 pb-16">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader><CardTitle>About</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed">{university.description}</p>
                </CardContent>
              </Card>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-5 text-center">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center mx-auto mb-2">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <p className="text-xl font-bold text-foreground">{(university.student_count || 0).toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground mt-1">Students</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-5 text-center">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center mx-auto mb-2">
                      <BookOpen className="w-5 h-5 text-emerald-600" />
                    </div>
                    <p className="text-xl font-bold text-foreground">{(university.course_count || 0).toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground mt-1">Courses</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-5 text-center">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mx-auto mb-2">
                      <Star className="w-5 h-5 text-amber-500" />
                    </div>
                    <p className="text-xl font-bold text-foreground">{university.average_rating?.toFixed(1) || 'N/A'}</p>
                    <p className="text-xs text-muted-foreground mt-1">Avg Rating</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-4">
              <Card>
                <CardHeader><CardTitle className="text-base">Contact Information</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {university.website && (
                    <a
                      href={university.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-[#003366] hover:underline"
                    >
                      <Globe className="w-4 h-4 shrink-0" />
                      <span className="truncate">{university.website.replace(/^https?:\/\//, '')}</span>
                      <ExternalLink className="w-3 h-3 shrink-0" />
                    </a>
                  )}
                  {university.email && (
                    <a
                      href={`mailto:${university.email}`}
                      className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
                    >
                      <Mail className="w-4 h-4 shrink-0" />
                      {university.email}
                    </a>
                  )}
                  {university.phone && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Phone className="w-4 h-4 shrink-0" />
                      {university.phone}
                    </div>
                  )}
                  {university.location && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="w-4 h-4 shrink-0" />
                      {university.location}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Link to={`/register?role=student`}>
                <Button className="w-full bg-[#003366] hover:bg-[#003366]/90">
                  Apply Now
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Courses Tab */}
        {activeTab === 'courses' && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">
              Available Courses ({(university.courses || []).length})
            </h2>
            {(university.courses || []).length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No courses listed yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(university.courses || []).map((course) => (
                  <Card key={course.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-semibold text-foreground flex-1 pr-2">{course.title}</h3>
                        {course.is_free ? (
                          <Badge className="bg-emerald-100 text-emerald-700 shrink-0">Free</Badge>
                        ) : (
                          <span className="text-sm font-bold text-[#003366] shrink-0">
                            {course.tuition_fee || formatMoney(course.price, course.currency)}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {course.level && (
                          <Badge className={`text-xs ${levelColors[course.level] || 'bg-gray-100 text-gray-700'}`}>
                            {course.level.charAt(0).toUpperCase() + course.level.slice(1)}
                          </Badge>
                        )}
                        {course.duration && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            {course.duration}
                          </span>
                        )}
                      </div>
                      <Button
                        size="sm"
                        className="w-full bg-[#003366] hover:bg-[#003366]/90"
                        onClick={() => isAuthenticated ? setApplyModal({ course }) : navigate('/login')}
                      >
                        Apply
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                Reviews ({(university.ratings || []).length})
              </h2>
              {university.average_rating > 0 && (
                <div className="flex items-center gap-2">
                  <StarRating rating={university.average_rating} />
                  <span className="font-bold">{university.average_rating?.toFixed(1)}</span>
                </div>
              )}
            </div>

            {/* Review List */}
            <div className="space-y-4">
              {(university.ratings || []).length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Star className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No reviews yet. Be the first to review!</p>
                  </CardContent>
                </Card>
              ) : (
                (university.ratings || []).map((review) => (
                  <Card key={review.id}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-sm">{review.reviewer_name || 'Anonymous'}</p>
                          <p className="text-xs text-muted-foreground">
                            {review.created_at?.slice(0, 10)}
                          </p>
                        </div>
                        <StarRating rating={review.rating} size="small" />
                      </div>
                      {review.comment && (
                        <p className="text-sm text-muted-foreground">{review.comment}</p>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            {/* Write a Review — only for authenticated users */}
            {isAuthenticated ? (
              <Card className="border-2 border-blue-100">
                <CardHeader>
                  <CardTitle className="text-base">Write a Review</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={submitReview} className="space-y-4">
                    <div>
                      <p className="text-sm font-medium mb-2">Your Rating</p>
                      <StarSelector
                        value={reviewForm.rating}
                        onChange={(val) => setReviewForm({ ...reviewForm, rating: val })}
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-2">Your Review</p>
                      <textarea
                        className="w-full border rounded-lg p-3 text-sm resize-none outline-none focus:ring-2 focus:ring-[#003366]/20 focus:border-[#003366]"
                        rows={4}
                        placeholder="Share your experience with this institution..."
                        value={reviewForm.comment}
                        onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={submittingReview || reviewForm.rating === 0}
                      className="bg-[#003366] hover:bg-[#003366]/90"
                    >
                      {submittingReview ? 'Submitting...' : 'Submit Review'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-blue-50 border-blue-100">
                <CardContent className="p-5 text-center">
                  <p className="text-sm text-muted-foreground mb-3">
                    Sign in to write a review
                  </p>
                  <Link to="/login">
                    <Button variant="outline" size="sm" className="border-[#003366] text-[#003366]">
                      Sign In to Review
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      <Dialog open={!!applyModal} onOpenChange={() => setApplyModal(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Apply: {applyModal?.course?.title}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitCourseApplication} className="space-y-4">
            <div className="space-y-2">
              <Label>Intake</Label>
              <Select value={applyForm.intake} onValueChange={v=>setApplyForm(f=>({...f,intake:v}))}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  {['September 2025','January 2026','September 2026','January 2027'].map(i=>(
                    <SelectItem key={i} value={i}>{i}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Personal Statement *</Label>
              <Textarea
                value={applyForm.personal_statement}
                onChange={e=>setApplyForm(f=>({...f,personal_statement:e.target.value}))}
                rows={5}
                placeholder="Tell the university why you want to study this course and what makes you a strong candidate..."
                required
              />
            </div>
            <div className="flex items-center gap-3">
              <input type="checkbox" id="visa" checked={applyForm.visa_required} onChange={e=>setApplyForm(f=>({...f,visa_required:e.target.checked}))} className="w-4 h-4"/>
              <Label htmlFor="visa">I require a student visa</Label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={()=>setApplyModal(null)}>Cancel</Button>
              <Button type="submit" disabled={applySubmitting} className="bg-[#003366] hover:bg-[#003366]/90">
                {applySubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : null}Submit Application
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
